import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Button, Typography } from "@packages/ui";
import PhotoCamera from "@material-ui/icons/PhotoCamera";
import VirComfirmDialog from "../VirConfirmDialog/VirConfirmDialog";
import { useDispatch, useSelector } from "react-redux";
import { Grid, Box, IconButton } from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import { ATHENA_VIR_PREPARE_THERMEON } from "src/types/actions";
import Lightbox from "react-image-lightbox";
import { fileToBase64 } from "src/utils/uploadPhotos";

const DEFAULT_UPLOAD_CONTENT = (
  <>
    By selecting &apos;confirm&apos;, you agree to upload only photos that depict damage to the
    rental vehicle, any other vehicles or property involved, or the surrounding incident scene.
    Please <span style={{ fontWeight: "bold", textDecoration: "underline" }}>do not</span> upload
    photos that contain personally identifiable information (PII), such as driver&apos;s licences.
  </>
);

const getSafeImageSrc = (photo) => {
  if (!photo) {
    return "";
  }
  if (photo instanceof File || photo instanceof Blob) {
    try {
      return URL.createObjectURL(photo);
    } catch {
      return "";
    }
  }
  if (typeof photo === "string") {
    return photo;
  }
  if (typeof photo === "object") {
    if (typeof photo.url === "string") {
      return photo.url;
    }
    if (typeof photo.preview === "string") {
      return photo.preview;
    }
    if (typeof photo.uri === "string") {
      return photo.uri;
    }
  }
  return "";
};

const VirImageButton = ({
  disabled,
  category,
  imageName,
  index,
  title = "Take or upload photos",
  content = DEFAULT_UPLOAD_CONTENT,
  driverLicense = "",
  execute = Function.prototype,
}) => {
  const dispatch = useDispatch();
  const hiddenFileInput = useRef(null);
  const athenaRole = useSelector((state) => state.vir.athenaRole.role);

  // Redux selectors
  const patchedDetails = useSelector((state) => state.vir?.athenaVirDetailPatch);
  const athenaVirDetail = useSelector((state) => state.vir?.athenaVirDetail);
  const resubmitFlag = useSelector((state) => state.vir?.athenaVirResubmit?.flag);
  const virPrepareTHERMEON = useSelector((state) => state.vir?.virPrepareTHERMEON);

  // Get previousData and existingPhotos
  const previousData = useMemo(
    () => (resubmitFlag ? athenaVirDetail?.data : patchedDetails?.data),
    [resubmitFlag, athenaVirDetail, patchedDetails]
  );
  const existingPhotos = useSelector((state) => {
    const cat = state.vir?.virPrepareTHERMEON[category];
    return cat && cat[index] ? cat[index].photos || [] : [];
  });

  // Local state
  const [openUploadConfirm, setOpenUploadConfirm] = useState(false);
  const [hiddenInputLoading, setHiddenInputLoading] = useState(false);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // Combine backend and redux photos
  const photos = useMemo(() => {
    if (category !== "damage") {
      return existingPhotos;
    }
    const backendPhotos =
      previousData?.PhotoBatch?.flatMap((batch) =>
        batch.photoUrls.map((photo) => ({
          ...photo,
          batchId: batch.batchId,
          author: batch.author,
        }))
      ) || [];
    return [...backendPhotos, ...existingPhotos];
  }, [category, previousData, existingPhotos]);

  // Photo URLs for lightbox
  const photoURLs = useMemo(() => photos.map((photo) => getSafeImageSrc(photo)).filter(Boolean), [
    photos,
  ]);

  // Notify parent on photo change
  useEffect(() => {
    if (!disabled) {
      execute(photos.length > 0);
    }
  }, [photos, disabled, execute]);

  // Handle file upload
  const handleAddDocuments = useCallback(
    async (event) => {
      const uploadedFiles = Array.from(event.target.files);
      if (!uploadedFiles.length) {
        return;
      }

      setHiddenInputLoading(true);
      const base64Files = await Promise.all(uploadedFiles.map((file) => fileToBase64(file)));
      const metadata = uploadedFiles.map((file, idx) => ({
        name: imageName + idx || file.name,
        size: file.size,
        type: file.type,
        lastModified: file.lastModified,
        preview: base64Files[idx],
        driverLicense,
      }));

      const newPhotos = [...existingPhotos, ...metadata];
      const payload = {
        [category]: {
          ...virPrepareTHERMEON[category],
          [index]: { photos: newPhotos },
        },
      };
      dispatch({ type: ATHENA_VIR_PREPARE_THERMEON, payload });
      setHiddenInputLoading(false);
    },
    [category, driverLicense, existingPhotos, imageName, index, virPrepareTHERMEON, dispatch]
  );

  // Handle delete
  const handleDeletePhoto = useCallback(
    (indexVal) => {
      if (indexVal < 0 || indexVal >= photos.length) {
        return;
      }
      const targetFile = photos[indexVal];
      if (!targetFile.batchId) {
        const newPhotos = photos.filter((_, i) => i !== indexVal);
        const remainingNewPhotos = newPhotos.filter((photo) => !photo.batchId);
        const payload = {
          [category]: {
            ...virPrepareTHERMEON[category],
            [index]: { photos: remainingNewPhotos },
          },
        };
        dispatch({ type: ATHENA_VIR_PREPARE_THERMEON, payload });
      }
    },
    [photos, category, index, virPrepareTHERMEON, dispatch]
  );

  // Uploader display
  const displayUploader = useMemo(() => {
    if (athenaRole === "customer") {
      return (author) => (author === "customer" ? "You" : "Staff");
    } else {
      return (author) => (author === "customer" ? "Customer" : "Staff");
    }
  }, []);

  return (
    <>
      <Button
        disabled={disabled}
        variant="contained"
        component="label"
        startIcon={<PhotoCamera />}
        fullWidth
        onClick={() => setOpenUploadConfirm(true)}
      >
        {title}
      </Button>
      <input
        type="file"
        hidden
        multiple
        ref={hiddenFileInput}
        accept="image/*"
        onChange={(event) => {
          handleAddDocuments(event);
          event.target.value = "";
        }}
      />

      <Box sx={{ mt: 3 }} />
      {!disabled && category === "damage" && (
        <Grid container spacing={2} sx={{ mt: 2 }}>
          {Object.entries(
            photos.reduce((acc, photo) => {
              const batchId = photo.batchId || "newUploads";
              if (!acc[batchId]) {
                acc[batchId] = [];
              }
              acc[batchId].push(photo);
              return acc;
            }, {})
          ).map(([batchId, batchPhotos]) => {
            const firstPhotoTimestamp = batchPhotos[0]?.timestamp
              ? new Date(batchPhotos[0].timestamp).toLocaleString()
              : "";
            return (
              <Grid container item xs={12} key={batchId}>
                {batchId !== "newUploads" && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" sx={{ fontWeight: "bold", mt: 1 }}>
                      Uploaded by: {displayUploader(batchPhotos[0]?.author)} at{" "}
                      {firstPhotoTimestamp}
                    </Typography>
                  </Grid>
                )}
                {batchPhotos.length > 2 ? (
                  <div
                    style={{
                      display: "flex",
                      overflowX: "auto",
                      gap: "10px",
                      padding: "10px 0",
                      scrollbarWidth: "thin",
                      msOverflowStyle: "none",
                    }}
                  >
                    {batchPhotos.map((photo, index) => {
                      const flatIndex = photos.findIndex((p) => p === photo);
                      return (
                        <div key={flatIndex} style={{ flex: "0 0 auto" }}>
                          <div
                            style={{
                              position: "relative",
                              width: "120px",
                              height: "80px",
                            }}
                          >
                            <img
                              src={getSafeImageSrc(photo)}
                              alt={`Preview ${flatIndex + 1}`}
                              onClick={() => {
                                setLightboxIndex(flatIndex);
                                setIsLightboxOpen(true);
                              }}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                borderRadius: "5px",
                                cursor: "pointer",
                                border: "2px solid",
                              }}
                            />
                            {!photo.batchId && (
                              <IconButton
                                onClick={() => handleDeletePhoto(flatIndex)}
                                style={{
                                  position: "absolute",
                                  top: "5px",
                                  right: "5px",
                                  backgroundColor: "white",
                                  borderRadius: "50%",
                                  padding: "5px",
                                }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <Grid container spacing={2}>
                    {batchPhotos.map((photo, index) => {
                      const flatIndex = photos.findIndex((p) => p === photo);
                      return (
                        <Grid item xs={4} key={flatIndex}>
                          <div
                            style={{
                              position: "relative",
                              width: "100%",
                              height: "80px",
                              marginTop: "20px",
                            }}
                          >
                            <img
                              src={getSafeImageSrc(photo)}
                              alt={`Preview ${flatIndex + 1}`}
                              onClick={() => {
                                setLightboxIndex(flatIndex);
                                setIsLightboxOpen(true);
                              }}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                borderRadius: "5px",
                                cursor: "pointer",
                                border: "2px solid",
                              }}
                            />
                            {!photo.batchId && (
                              <IconButton
                                onClick={() => handleDeletePhoto(flatIndex)}
                                style={{
                                  position: "absolute",
                                  top: "5px",
                                  right: "5px",
                                  backgroundColor: "white",
                                  borderRadius: "50%",
                                  padding: "5px",
                                }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            )}
                          </div>
                        </Grid>
                      );
                    })}
                  </Grid>
                )}
              </Grid>
            );
          })}
        </Grid>
      )}
      {isLightboxOpen && photoURLs.length > 0 && (
        <Lightbox
          mainSrc={photoURLs[lightboxIndex]}
          nextSrc={photoURLs[(lightboxIndex + 1) % photoURLs.length]}
          prevSrc={photoURLs[(lightboxIndex + photoURLs.length - 1) % photoURLs.length]}
          onCloseRequest={() => setIsLightboxOpen(false)}
          onMovePrevRequest={() =>
            setLightboxIndex((lightboxIndex + photoURLs.length - 1) % photoURLs.length)
          }
          onMoveNextRequest={() => setLightboxIndex((lightboxIndex + 1) % photoURLs.length)}
        />
      )}

      <VirComfirmDialog
        open={openUploadConfirm}
        handleClose={() => setOpenUploadConfirm(false)}
        title={category === "damage" ? "Vehicle Incident Photos" : "Driver's Licence Upload"}
        content={content}
        execute={() => {
          setHiddenInputLoading(true);
          hiddenFileInput.current.click();
          setHiddenInputLoading(false);
          setOpenUploadConfirm(false);
        }}
        loading={hiddenInputLoading}
        confirmText="Confirm"
      />
    </>
  );
};

export default VirImageButton;
